/**
 * PWA としての振る舞い。
 *
 *   - Service Worker の登録と、新しい版が出たときの扱い
 *   - 保存領域を消されにくくする要求（docs/04 §3）
 *
 * 設計の骨子は docs/04 §3 の「**消えないではなく、消えても即座に検知して直す**」。
 * Web の保存領域は OS の判断で消えうるので、消えない前提を置かない。
 */

import { registerSW } from 'virtual:pwa-register';
import { TRACK_CACHE_DB_NAME } from './live/track-cache-store.ts';

export interface PwaHooks {
  /** 新しい版が待機中。適用するとページが再読み込みされる */
  onUpdateReady: (apply: () => void) => void;
  /** オフラインでも起動できる状態になった */
  onOfflineReady: () => void;
}

/**
 * Service Worker を登録する。
 *
 * 自動更新にしていないのは、**飛行中に勝手にリロードされるのを避ける**ため。
 * 新しい版はダウンロードだけして待機させ、適用の判断は利用者に委ねる。
 */
export function setupPwa(hooks: PwaHooks): void {
  if (!('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    onNeedRefresh() {
      hooks.onUpdateReady(() => void updateSW(true));
    },
    onOfflineReady() {
      hooks.onOfflineReady();
    },
  });
}

export interface StorageState {
  /** 消されにくい領域として許可されたか */
  persisted: boolean;
  /** 使用量 [バイト] */
  usage: number | null;
  /** 割り当て上限 [バイト] */
  quota: number | null;
}

/**
 * 保存領域を「消されにくい」状態にするよう要求する。
 *
 * iOS Safari は、ホーム画面に追加していない場合 7 日間の未使用で
 * 保存領域を消す。ホーム画面へ追加してもらうことが**必須の運用手順**に
 * なるのはこのため（docs/04 §3）。
 *
 * `persist()` が false を返しても機能は落とさない。消えたときに検知して
 * 直せるようにしておくのが本筋で、ここはあくまで確率を下げる手段。
 */
export async function requestPersistentStorage(): Promise<StorageState> {
  const state: StorageState = { persisted: false, usage: null, quota: null };
  if (!navigator.storage) return state;

  try {
    if (navigator.storage.persisted) {
      state.persisted = await navigator.storage.persisted();
    }
    if (!state.persisted && navigator.storage.persist) {
      state.persisted = await navigator.storage.persist();
    }
    if (navigator.storage.estimate) {
      const est = await navigator.storage.estimate();
      state.usage = est.usage ?? null;
      state.quota = est.quota ?? null;
    }
  } catch {
    // 対応していないブラウザでは黙って既定値を返す
  }
  return state;
}

/** ホーム画面から起動されているか（＝ PWA としてインストール済みか） */
export function isInstalled(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari は display-mode を返さないので独自プロパティを見る
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** このアプリのビルド時刻。古い版で固まっていないかを見分けるために表示する */
export const BUILD_ID = __GV_BUILD_ID__;

/** サーバーが配っている版（`build-info.json`）。deploy/update.sh が書く */
export interface ServerBuildInfo {
  /** アプリに焼かれているのと同じ文字列（例 `2026-08-21 18:25 JST`） */
  buildId: string;
  /** デプロイしたコミット */
  sha?: string;
  /** デプロイ元のブランチ */
  branch?: string;
  deployedAt?: string;
}

/**
 * サーバーがいま配っている版を読む。
 *
 * ★ 必ず網越しに取ること（`cache: 'no-store'`）。Service Worker の
 *   プリキャッシュから返してしまうと、「サーバーの版」を知るという
 *   目的そのものが成り立たない（vite.config.ts の globIgnores も参照）。
 *
 * オフラインや、まだ build-info.json を置いていないサーバーでは null を返す。
 * **取れないことを失敗として扱わない**——従来どおりの判定に落とすだけにする。
 */
export async function fetchServerBuildInfo(): Promise<ServerBuildInfo | null> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}build-info.json`, { cache: 'no-store' });
    if (!res.ok) return null;
    const v: unknown = await res.json();
    if (typeof v !== 'object' || v === null) return null;
    const info = v as Partial<ServerBuildInfo>;
    return typeof info.buildId === 'string' ? (info as ServerBuildInfo) : null;
  } catch {
    return null;
  }
}

/**
 * 手動の更新確認の結果。
 *
 * `server-differs` は「サーバーは別の版を配っているのに、Service Worker が
 * 新しい版を用意できていない」状態。**サーバー側のデプロイが止まっている**
 * ときにこれが出る（2026-08-19〜21 に実際に起きた。deploy/update.sh が
 * 古いブランチを見ていて2日間デプロイされておらず、それでもアプリは
 * 「最新の版です」と言い続けていた）。
 */
export type UpdateCheck =
  | { status: 'updated' }
  | { status: 'current' }
  | { status: 'unavailable' }
  | { status: 'server-differs'; server: ServerBuildInfo };

/**
 * 新しい版が出ていないか、いますぐ確認する。
 *
 * 通常はページ読み込み時に自動で確認するが、Service Worker が古い版を
 * 配っている状態ではその確認自体が走らないことがある。手動で叩ける口を
 * 用意しておかないと、利用者は古い版に閉じ込められたまま何もできない。
 *
 * ★ Service Worker の差分だけでなく、**サーバーが配っている版**も見る。
 *   `reg.update()` は「sw.js のバイト列が変わったか」しか見ないので、
 *   サーバーが古ければ正しく「変わっていない」と答える。それを
 *   「最新の版です」と表示すると、利用者にはサーバーが止まっていることが
 *   一切分からない。
 */
export async function checkForUpdate(): Promise<UpdateCheck> {
  // Service Worker が使えなくても、サーバーの版だけは確認できる
  const serverPromise = fetchServerBuildInfo();

  /** SW 側で新版を用意できていないときに、サーバーとの食い違いを見る */
  const differsFromServer = async (): Promise<UpdateCheck> => {
    const server = await serverPromise;
    if (server && server.buildId && server.buildId !== BUILD_ID) {
      return { status: 'server-differs', server };
    }
    return { status: 'current' };
  };

  if (!('serviceWorker' in navigator)) return { status: 'unavailable' };
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return { status: 'unavailable' };
    await reg.update();

    if (reg.waiting) return { status: 'updated' };
    if (reg.installing) {
      // ★ update() は新しい版のダウンロード開始を指示するだけで、インストール
      //   完了（waiting状態）までは待ってくれない。ここで待たずに
      //   reg.installing の存在だけで「updated」と判定すると、まだ本物の
      //   onNeedRefresh（registerSW側）が発火する前に「更新する」ボタンを
      //   出してしまい、押しても中身が変わらない不具合になる（実機で発生）
      const worker = reg.installing;
      await new Promise<void>((resolve) => {
        const onStateChange = () => {
          if (worker.state === 'installed' || worker.state === 'redundant') {
            worker.removeEventListener('statechange', onStateChange);
            resolve();
          }
        };
        worker.addEventListener('statechange', onStateChange);
      });
    }
    return reg.waiting ? { status: 'updated' } : await differsFromServer();
  } catch {
    return { status: 'unavailable' };
  }
}

/**
 * 保存されているものを全部捨てて読み込み直す（最後の手段）。
 *
 * 古い版のキャッシュが壊れて起動しない・更新が効かない、といった状態から
 * 抜けるための脱出口。オフライン用のデータも消えるので、次回オンライン時に
 * 取り直しになる。
 */
export async function resetAndReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    // 端末内に残したライブ受信データ（live/track-cache-store.ts）も消す
    if ('indexedDB' in window) {
      await new Promise<void>((resolve) => {
        const req = indexedDB.deleteDatabase(TRACK_CACHE_DB_NAME);
        req.onsuccess = req.onerror = req.onblocked = () => resolve();
      });
    }
  } finally {
    // キャッシュを迂回して取り直す
    location.replace(`${location.pathname}?reset=${Date.now()}`);
  }
}
